import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  canShowPopup,
  canShowSpecificPopup,
  recordPopupShown,
  undoPopupShown,
  recordSpecificPopupShown,
  clearSpecificPopupShown,
  isHomeChecked,
  markHomeChecked,
  handleVisibilityChange,
  debugState,
} from "../utils/popupManager";
import { POPUP_REGISTRY, DATA_SOURCES } from "../utils/popupRegistry";
import { curStateAccount, curStateMiniGame } from "../Redux/selector";
import { handleCheckAutoLogin, isIntroTourCompleted } from "../helper/helper";
import { isNewUserFlowActive } from "../utils/introTour";
import { useLocation } from "react-router";
import PATH from "../config/PATH";
import { hasAuthCookie } from "../utils/auth";

// Popup decision logging — disabled in production builds. Flip DEBUG to true
// locally (or in dev) to inspect the priority resolution in DevTools.
const DEBUG = process.env.NODE_ENV !== "production";
const log = {
  log:            (...a) => { if (DEBUG) console.log(...a); },
  groupCollapsed: (...a) => { if (DEBUG) console.groupCollapsed(...a); },
  groupEnd:       (...a) => { if (DEBUG) console.groupEnd(...a); },
};

// Trang được mở từ MyID kèm tham số auto-login (msisdn/token trong index.html)
// và user chưa chủ động đăng xuất ở tab này (App.js dùng đúng điều kiện này).
// Bọc try/catch vì ngoài webview (test, trình duyệt thường) có thể không có input.
function isAutoLoginEntry() {
  try {
    return !!handleCheckAutoLogin() && !sessionStorage.getItem("logout");
  } catch (_) {
    return false;
  }
}

// Auto-login thất bại thì không có tín hiệu nào báo về — sau khoảng này mới coi
// là guest để popup dành cho guest vẫn có cơ hội hiện.
const AUTO_LOGIN_WAIT_MS = 8000;

const GATE_REASON_TEXT = {
  visit:       'popup already shown this visit',
  cooldown:    'cooldown active (1h since last popup shown)',
  daily_limit: 'daily limit reached (3 popups/day)',
};

// ─────────────────────────────────────────────────────────────────────────────
// ENGINE — component này KHÔNG biết popup nào tồn tại. Toàn bộ danh sách, thứ
// tự ưu tiên, tần suất, điều kiện đối tượng và cách render nằm ở
// utils/popupRules.js + utils/popupRegistry.jsx. Quy tắc "lần vào", giới hạn và
// cooldown nằm ở utils/popupManager.js.
//
// Yêu cầu nghiệp vụ gốc: sheet "Phân nhóm và tối ưu luồng người dùng" trong
// src/Mytel - ShweDeal - SHWEDEAL 2.0 UPGRADE.xlsx.
//
// Luồng:
//   1. Master gate     — không ở trang login; đã login (cả hàng đợi) hoặc guest
//                        (chỉ popup `guest: true`); có cookie mà /current chưa
//                        về thì đợi
//   2. Điểm hiển thị   — chỉ ở Home, và mỗi đối tượng (guest / user) chỉ được
//                        xét 1 lần mỗi lần vào. Tour đang chạy thì hoãn lượt xét
//   3. Global gate     — canShowPopup(): 1 popup/lần vào, cooldown 1 giờ tính từ
//                        lúc popup hiện, tối đa 3 popup/ngày. Chặn thì lượt xét
//                        của lần vào này kết thúc, không hiện gì
//   4. Lọc ứng viên    — popup đang bật + đúng đối tượng + còn hạn mức + chưa skip
//   5. Fetch data      — chỉ fetch `needs` của các ứng viên còn sống. Ngoài lượt
//                        xét thì chỉ fetch needs của popup fetchOnEveryLoad (pu6),
//                        1 lần mỗi lần tải trang
//   6. Chọn 1 popup    — ứng viên priority cao nhất có isEligible() === true
// ─────────────────────────────────────────────────────────────────────────────
function ModalRender() {
  const dispatch = useDispatch();
  const userInfo = useSelector(curStateAccount).userInfo;
  const miniGameConfig = useSelector(curStateMiniGame).configMiniGame;
  const { typeIntroTour } = useSelector((state) => state.reducerHome);
  const { pathname } = useLocation();

  // id của popup thắng vòng ưu tiên (null = không hiện gì)
  const [activeId, setActiveId] = useState(null);
  // Ref để vòng ưu tiên đọc được activeId hiện tại mà không phải đưa nó vào dep
  // array (sẽ gây re-run và có nguy cơ ghi đè popup đang mở).
  const activeIdRef = useRef(null);
  activeIdRef.current = activeId;
  // Token của recordPopupShown() cho popup đang mở — để skip hoàn tác được.
  const shownTokenRef = useRef(null);

  // data async đã fetch cho quyết định, key theo DATA_SOURCES
  const [data, setData] = useState({});
  // "Bộ fetch" mà `data` đang phản ánh. Chỉ coi là ready khi khớp bộ fetch hiện
  // tại — nếu dùng cờ boolean, vòng ưu tiên có thể chạy ngay trong lượt commit
  // mà tập needs vừa đổi, với data CŨ còn thiếu key, và chọn sai popup.
  const [readyKey, setReadyKey] = useState(null);
  // Key đã fetch trong lần tải trang này (ModalRender mount 1 lần ở LayoutApp)
  // — nhánh fetchOnEveryLoad dựa vào đây để mỗi lần tải trang chỉ gọi 1 lần.
  const fetchedThisLoadRef = useRef(new Set());
  // Trạng thái lần vào nằm ở sessionStorage (popupManager) — tăng tick để render
  // lại mỗi khi nó đổi (vừa xét xong ở Home, hoặc bắt đầu lần vào mới).
  const [visitTick, setVisitTick] = useState(0);
  const bumpVisit = () => setVisitTick((t) => t + 1);
  // Popup tự báo "không có gì để hiện". Không tính là một lần hiển thị — engine
  // loại nó khỏi ứng viên rồi xét tiếp popup kế ngay trong lượt xét đó.
  const [skippedIds, setSkippedIds] = useState(() => new Set());
  const [retryAfterSkip, setRetryAfterSkip] = useState(false);

  // Derive userId so the effect body doesn't reference `userInfo` directly,
  // keeping the dependency array precise (only re-fetch on user identity change).
  const userId = userInfo?.id;

  // ── 0. Master gate: KHÔNG đứng ở trang login (luồng xác thực — popup chen vào
  //      sẽ che form), và đã biết chắc là user đã login hay guest:
  //        - đã login (có userInfo.id): chạy toàn bộ hàng đợi (spec 2a)
  //        - guest (không userId, không cookie token): chỉ popup `guest: true`
  //          trong popupRules.js (spec 2b), tần suất tính theo thiết bị
  //        - có cookie nhưng /current chưa về: ĐỢI — nếu coi là guest thì popup
  //          guest sẽ chiếm suất trước khi hàng đợi của user kịp chạy.
  //        - mở từ MyID mà chưa có cookie (lần đầu / cookie hết hạn): LayoutApp
  //          đang auto-login — cũng ĐỢI, tối đa AUTO_LOGIN_WAIT_MS.
  const isLoginPage = pathname?.startsWith(PATH.LOGIN);
  const autoLoginPending = !userId && !hasAuthCookie() && isAutoLoginEntry();
  const [autoLoginTimedOut, setAutoLoginTimedOut] = useState(false);
  useEffect(() => {
    if (!autoLoginPending || autoLoginTimedOut) return undefined;
    const timer = setTimeout(() => {
      log.log('[ModalRender] ⏱ auto-login chưa xong sau 8s — coi là guest');
      setAutoLoginTimedOut(true);
    }, AUTO_LOGIN_WAIT_MS);
    return () => clearTimeout(timer);
  }, [autoLoginPending, autoLoginTimedOut]);
  const isGuest = !userId && !hasAuthCookie() && (!autoLoginPending || autoLoginTimedOut);
  const audience = userId ? `user:${userId}` : isGuest ? "guest" : null;
  const popupsAllowed = !!audience && !isLoginPage;

  // ── 0b. Luồng người mới: popup mời bắt đầu tour đang chờ user chọn
  //      (NewUserTourInvite — nằm NGOÀI popup manager) hoặc tour đang chạy thì
  //      KHÔNG popup nào được hiện — tại một thời điểm chỉ có đúng một thứ chiếm
  //      màn hình. Lượt xét ở Home được HOÃN (không tính là đã xét) tới khi xong.
  const newUserFlowActive = isNewUserFlowActive({ typeIntroTour, userInfo });

  // ── 0c. Lần vào mới khi quay lại từ background sau ≥ 1 giờ (spec mục 1).
  useEffect(() => {
    const onVisibility = () => {
      if (handleVisibilityChange(document.visibilityState)) {
        log.log('[ModalRender] 🔄 quay lại sau ≥ 1 giờ ở background — bắt đầu lần vào mới');
        setSkippedIds((prev) => (prev.size ? new Set() : prev));
        bumpVisit();
      }
    };
    const onPageHide = () => handleVisibilityChange("hidden");
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  // ── 0d. Điểm hiển thị: chỉ ở Home, ở lần đầu đối tượng này về Home trong lần
  //      vào (spec mục 4, 5). Sau khi skip thì xét tiếp trong cùng lượt.
  const isHome = pathname === PATH.HOME;
  const homeCheckPending =
    popupsAllowed && isHome && !newUserFlowActive &&
    (retryAfterSkip || !isHomeChecked(audience));

  // ── 1. Lọc ứng viên ─────────────────────────────────────────────────────────
  // Chỉ giữ popup đang bật, đúng đối tượng (guest chỉ lấy popup `guest: true`),
  // còn hạn mức tần suất và chưa tự skip ở lượt này.
  // Điều kiện đối tượng (isEligible) chưa chạy ở đây vì có thể cần data async.
  const candidates = useMemo(() => {
    if (!popupsAllowed) return [];
    return POPUP_REGISTRY.filter(
      (p) =>
        p.enabled &&
        (!!userId || p.guest) &&
        !skippedIds.has(p.id) &&
        canShowSpecificPopup(p.id, userId),
    );
  }, [popupsAllowed, userId, skippedIds, visitTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Union các `needs` của ứng viên — popup bị tắt hoặc hết hạn mức không kéo
  // theo API nào. Join thành chuỗi để làm dep ổn định cho effect fetch.
  const neededKeys = useMemo(
    () => [...new Set(candidates.flatMap((p) => p.needs || []))].sort(),
    [candidates],
  );
  const neededKeysId = neededKeys.join(",");

  // Key của các popup khai báo fetchOnEveryLoad — vẫn fetch ngoài lượt xét.
  const alwaysFetchKeysId = useMemo(
    () =>
      [...new Set(
        candidates.filter((p) => p.fetchOnEveryLoad).flatMap((p) => p.needs || []),
      )].sort().join(","),
    [candidates],
  );

  // Bộ fetch hiện tại — xem readyKey.
  const fetchKey = `${audience}|${neededKeysId}|${homeCheckPending ? "check" : "idle"}|${visitTick}`;
  const ready = readyKey === fetchKey;

  // ── 2. Fetch data cần cho quyết định ────────────────────────────────────────
  useEffect(() => {
    if (!popupsAllowed) {
      log.log(`[ModalRender] 🚫 popup manager off — loggedIn:${!!userId} loginPage:${isLoginPage}`);
      // Huỷ luôn popup đang chọn (nếu có) để lúc quay lại trang thường không
      // render tiếp lựa chọn cũ. Xoá cả danh sách skip — nếu không, popup đã skip
      // một lần sẽ bị loại suốt cả session SPA dù dữ liệu của nó đã đổi.
      setReadyKey(null);
      setActiveId(null);
      setSkippedIds((prev) => (prev.size ? new Set() : prev));
      return;
    }

    let cancelled = false;

    if (homeCheckPending && canShowPopup()) {
      const fetchAll = async () => {
        neededKeys.forEach((k) => fetchedThisLoadRef.current.add(k));
        const entries = await Promise.all(
          neededKeys.map(async (key) => [key, await DATA_SOURCES[key]()]),
        );

        if (cancelled) return;

        const next = Object.fromEntries(entries);
        setData(next);

        log.groupCollapsed('[ModalRender] 📡 fetch complete');
        log.log('audience:', audience);
        log.log('fetched keys:', neededKeys.length ? neededKeys : '(none needed)');
        log.log('data:', next);
        log.log('pm state:', debugState(userId));
        log.groupEnd();

        setReadyKey(fetchKey);
      };
      fetchAll();
      return () => { cancelled = true; };
    }

    if (homeCheckPending) {
      // Lần đầu về Home mà global gate chặn → lượt xét của lần vào này kết thúc,
      // không hiện popup (spec mục 3, 4: xét lại ở lần vào sau).
      const s = debugState(userId);
      log.groupCollapsed('[ModalRender] 🚫 home check blocked — global gate');
      log.log('reason:', GATE_REASON_TEXT[s.gateBlockReason], s.cooldownRemaining, s.popupsToday);
      log.log('full state:', s);
      log.groupEnd();
      markHomeChecked(audience);
      setRetryAfterSkip(false);
      bumpVisit();
    }

    // Ngoài lượt xét, popup fetchOnEveryLoad (pu6) vẫn phải gọi API mỗi lần tải
    // trang. Mỗi key chỉ gọi 1 lần/lần tải.
    const keys = (alwaysFetchKeysId ? alwaysFetchKeysId.split(",") : [])
      .filter((k) => !fetchedThisLoadRef.current.has(k));
    if (keys.length) {
      keys.forEach((k) => fetchedThisLoadRef.current.add(k));
      Promise.all(keys.map(async (k) => [k, await DATA_SOURCES[k]()])).then((entries) => {
        if (cancelled) return;
        log.log('[ModalRender] 📡 fetchOnEveryLoad:', keys);
        setData((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      });
    }
    return () => { cancelled = true; };
    // fetchKey gói: đối tượng (guest/user), tập needs, có đang trong lượt xét ở
    // Home không, và lần vào hiện tại
  }, [fetchKey, popupsAllowed, isLoginPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 3. Vòng ưu tiên ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!homeCheckPending) return;
    if (!ready) return;
    if (activeIdRef.current !== null) return; // đừng ghi đè popup đang mở

    if (!canShowPopup()) {
      markHomeChecked(audience);
      setRetryAfterSkip(false);
      bumpVisit();
      return;
    }

    // isTourCompleted: guide tour onboarding chưa xong thì không chen popup vào.
    const ctx = {
      userInfo,
      userId,
      miniGameConfig,
      typeIntroTour,
      data,
      pathname,
      isTourCompleted: isIntroTourCompleted(typeIntroTour),
    };

    log.groupCollapsed('[ModalRender] 🔍 priority resolution');
    log.log('pm state:', debugState(userId));

    // POPUP_REGISTRY đã sort sẵn theo priority nên `find` đầu tiên chính là
    // popup ưu tiên cao nhất thoả điều kiện.
    const winner = candidates.find((p) => {
      let ok = false;
      try {
        ok = !!p.isEligible(ctx);
      } catch (err) {
        // Một popup lỗi điều kiện không được làm chết cả hàng đợi.
        log.log(`P${p.priority} ${p.id}: ⚠️ isEligible threw —`, err);
        ok = false;
      }
      log.log(`P${p.priority} ${p.id}: ${ok ? '✅ SELECTED' : '❌ skip'}`);
      return ok;
    });

    // Lượt xét ở Home của đối tượng này trong lần vào đã xong, dù có popup hay không.
    markHomeChecked(audience);
    setRetryAfterSkip(false);

    if (winner) {
      // Chiếm suất "1 popup / lần vào", bắt đầu cooldown, +1 popup trong ngày và
      // ghi hạn mức tần suất NGAY tại đây, không đợi user đóng: nếu đợi, user
      // reload hoặc thoát giữa chừng sẽ không ghi được gì.
      // Popup thua priority không bao giờ được render nên không bị trừ oan.
      // Popup được chọn rồi tự skip sẽ được hoàn tác trong handleSkip().
      shownTokenRef.current = recordPopupShown();
      recordSpecificPopupShown(winner.id, userId);
      setActiveId(winner.id);
    } else {
      log.log('→ no popup selected');
      setActiveId(null);
    }
    log.groupEnd();
    bumpVisit();
  }, [homeCheckPending, ready, candidates, userInfo, miniGameConfig, data, userId, typeIntroTour, pathname, audience]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Callbacks ───────────────────────────────────────────────────────────────

  // User đóng popup. Cooldown đã tính từ lúc popup hiện, hạn mức cũng đã ghi.
  const handleClose = () => {
    shownTokenRef.current = null;
    setActiveId(null);
  };

  // Popup báo không có gì để hiện → KHÔNG tính là một lần hiển thị (spec mục 3):
  // trả lại suất lần vào, cooldown, số popup trong ngày và hạn mức tần suất.
  // Loại nó khỏi ứng viên rồi xét tiếp popup kế trong cùng lượt xét.
  const handleSkip = (id) => {
    log.log(`[ModalRender] ${id}: ⏭ skipped (không có nội dung) — chạy tiếp hàng đợi`);
    undoPopupShown(shownTokenRef.current);
    shownTokenRef.current = null;
    clearSpecificPopupShown(id, userId);
    setSkippedIds((prev) => new Set(prev).add(id));
    setRetryAfterSkip(true);
    setActiveId(null);
  };

  // Popup tự xác nhận đã hiện thật. Hạn mức đã ghi lúc chọn nên đây là ghi lại
  // cho chắc (idempotent) — giữ để popup tự quyết định nội dung ở runtime vẫn có
  // đường báo ngược về engine.
  const handleMarkShown = (id) => {
    recordSpecificPopupShown(id, userId);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (!popupsAllowed) return null;

  // Luồng người mới đang chiếm màn hình (popup mời / tour) thì không render popup
  // nào, kể cả popup đã được chọn từ trước.
  if (newUserFlowActive) return null;

  if (!activeId) return null;

  const active = POPUP_REGISTRY.find((p) => p.id === activeId);
  if (!active) return null;

  return active.render({
    data,
    userInfo,
    userId,
    dispatch,
    close:     handleClose,
    skip:      () => handleSkip(active.id),
    markShown: () => handleMarkShown(active.id),
  });
}

export default ModalRender;
